import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {RelationType, RelationTypeToName} from 'common/src/modules/relatedItems/relatedItems.types';
import {clinchEnums} from '@cl/shared';
import {CLEnumKeys, CLEnumType} from 'common/src/modules/enums/enums.types';
import getProductStrategyRelatedLogics from 'common/src/modules/campaigns/utils/getProductStrategyRelatedLogics';

const setRelatedItemToolSchema = z.object({
  relatedItemLogicId: z.number().describe('The related item logic ID to use'),
});

export default function setRelatedItemTool(relationType: RelationType) {
  async function execute({
    input: {relatedItemLogicId},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof setRelatedItemToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    const feedId = validatedScratchpad.card.feedId;

    if (!feedId) {
      return toolError(`Feed is not set for this card`);
    }

    const strategy = validatedScratchpad.card.productsSelectionStrategy;

    if (!strategy) {
      return toolError(`Strategy is not set for this card. Please set a strategy first.`);
    }

    // Validate that the strategy supports this relation type
    const campaignEnumMapper = clinchEnums.loadedEnumsSubject.data[CLEnumType.CampaignPage];

    if (!campaignEnumMapper) {
      return toolError('Strategies enum is not loaded');
    }

    const {isTopItemsOriented, isCategoryTopItemsOriented, isRetargetingOriented} =
      getProductStrategyRelatedLogics(campaignEnumMapper, strategy);

    // Check if the strategy supports the relation type
    let isSupported = false;
    const relationTypeName = RelationTypeToName[relationType];

    switch (relationType) {
      case RelationType.ProductSet:
        isSupported = isRetargetingOriented;
        break;
      case RelationType.CategoryTopItems:
        isSupported = isCategoryTopItemsOriented;
        break;
      case RelationType.TopItems:
        isSupported = isTopItemsOriented;
        break;
    }

    if (!isSupported) {
      const strategyName =
        campaignEnumMapper[CLEnumKeys.CampaignPage.CampaignContentType]?.byValue[strategy]
          ?.uiName || `strategy ${strategy}`;

      return toolError(
        `The selected strategy "${strategyName}" does not support ${relationTypeName}. Please choose a different strategy or use a different configuration.`,
      );
    }

    const availableRelatedItemLogics = validatedScratchpad.card.relatedItemsLogics?.filter(
      (r) => r.relationType === relationType,
    );

    if (!availableRelatedItemLogics?.some((p) => p.id === relatedItemLogicId)) {
      return toolError(
        `Related item logic with ID ${relatedItemLogicId} is not available for this card. Available related item logics: ${availableRelatedItemLogics?.map((p) => `${p.id}: ${p.name}`).join(', ')}`,
      );
    }

    switch (relationType) {
      case RelationType.ProductSet:
        validatedScratchpad.card.retargetingProductSetId = relatedItemLogicId;
        break;
      case RelationType.CategoryTopItems:
        validatedScratchpad.card.categoryTopItemsLogicId = relatedItemLogicId;
        break;
      case RelationType.TopItems:
        validatedScratchpad.card.topItemLogicId = relatedItemLogicId;
        break;
    }

    return toolResult(
      `${relationTypeName} set to: ${relatedItemLogicId} (${availableRelatedItemLogics?.find((p) => p.id === relatedItemLogicId)?.name})`,
    );
  }

  return tool({
    name: `${V2CardAgentToolName.SetRelatedItemLogic}${RelationTypeToName[relationType]?.trim().toLowerCase().replace(/ /g, '')}`,
    displayName: `Set ${RelationTypeToName[relationType]}`,
    description: `Set the ${RelationTypeToName[relationType]} for the card`,
    execute,
    inputSchema: setRelatedItemToolSchema,
  });
}
